import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { Loader2, AlertTriangle, CheckCircle2 } from "lucide-react";

/**
 * Landing für Notfall-QR-Scan.
 * URL: /notfall-scan/NF-20260206-0001
 *
 * Prüft ob Nummer existiert → wenn nicht eingeloggt: Login-Redirect mit ?next=
 * Wenn eingeloggt + Rechte: leitet weiter zu /admin/notfall-rebook?nr=NUMMER
 */
export default function NotfallScan() {
  const { number } = useParams();
  const nav = useNavigate();
  const [status, setStatus] = useState("loading"); // loading | ok | used | notfound | forbidden
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/notfall-blanks/lookup/${encodeURIComponent(number)}`);
        if (!alive) return;
        setInfo(data);
        if (data?.blank?.status === "used") setStatus("used");
        else setStatus("ok");
        // Automatisch weiterleiten wenn unbenutzt
        if (data?.blank?.status !== "used") {
          setTimeout(() => nav(`/admin/notfall-rebook?nr=${encodeURIComponent(number)}`, { replace: true }), 1400);
        }
      } catch (e) {
        if (!alive) return;
        if (e.response?.status === 401 || e.response?.status === 403) {
          // Login erforderlich
          nav(`/login?next=${encodeURIComponent(`/notfall-scan/${number}`)}`, { replace: true });
        } else if (e.response?.status === 404) {
          setStatus("notfound");
          setErr(e.response?.data?.detail || `Notfall-Nr. ${number} nicht gefunden`);
        } else {
          setStatus("forbidden");
          setErr(e.response?.data?.detail || "Zugriff verweigert");
        }
      }
    })();
    return () => { alive = false; };
  }, [number, nav]);

  return (
    <div className="min-h-screen bg-[#050505] text-[#F5F0E6] flex items-center justify-center p-6" data-testid="notfall-scan-page">
      <div className="max-w-md w-full card-lux p-8 text-center space-y-5">
        <div className="text-[10px] uppercase tracking-[0.4em] text-[#EE9CA1]">Notfall · Scan</div>
        <div className="font-heading text-2xl sm:text-3xl">{number}</div>

        {status === "loading" && (
          <div className="flex flex-col items-center gap-3 py-6">
            <Loader2 size={28} className="animate-spin text-[#D4AF37]" />
            <div className="text-sm text-[#A8A398]">Prüfe Notfall-Ticket …</div>
          </div>
        )}

        {status === "ok" && (
          <div className="flex flex-col items-center gap-3 py-6">
            <CheckCircle2 size={36} className="text-[#8FBC8F]" />
            <div className="text-sm text-[#8FBC8F] uppercase tracking-widest">Blank gefunden — bitte nachbuchen</div>
            <div className="text-xs text-[#A8A398]">Du wirst zur Nachbuch-Maske weitergeleitet …</div>
          </div>
        )}

        {status === "used" && (
          <div className="flex flex-col items-center gap-3 py-6">
            <AlertTriangle size={36} className="text-[#EEDC82]" />
            <div className="text-sm text-[#EEDC82] uppercase tracking-widest">Bereits nachgebucht</div>
            {info?.used_order && (
              <div className="text-xs text-[#A8A398] font-mono">
                {info.used_order.order_number} · {info.used_order.movie_title}
              </div>
            )}
            <button onClick={() => nav("/admin/notfall-rebook")}
              className="mt-2 text-[10px] uppercase tracking-[0.32em] px-4 py-2 border border-[#D4AF37]/60 text-[#D4AF37] rounded hover:bg-[#D4AF37] hover:text-black">
              Zur Nachbuch-Übersicht
            </button>
          </div>
        )}

        {(status === "notfound" || status === "forbidden") && (
          <div className="flex flex-col items-center gap-3 py-6">
            <AlertTriangle size={36} className="text-[#EE9CA1]" />
            <div className="text-sm text-[#EE9CA1]">{err}</div>
            <button onClick={() => nav("/admin/notfall-rebook")}
              className="mt-2 text-[10px] uppercase tracking-[0.32em] px-4 py-2 border border-[#2A2A2A] rounded hover:border-[#D4AF37]">
              Trotzdem manuell nachbuchen
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
